import { zodResolver } from '@hookform/resolvers/zod';
import {
  useForm,
  FieldValues,
  FormState,
  UseFormReturn,
  UseFormProps,
  Path,
  PathValue,
  FieldPathValue,
  DefaultValues,
  RegisterOptions,
  Resolver,
} from 'react-hook-form';
import { getZodDefaults } from '@repo/util/getZodDefaults';
import { TypeOf, z } from 'zod';
import { ZodObjecty, TypeOfOptional } from '@repo/types/Types';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { isValid, format } from 'date-fns';

/** What a form asks before submitting. See `UseZodFormProps.confirm`. */
export interface FormConfirmationRequest {
  title: string;
  description?: string;
  /** Defaults to "Confirm" */
  confirmText?: string;
  /** Defaults to "Cancel" */
  cancelText?: string;
  /** `danger` styles the confirm button red; keep it for destructive actions */
  variant?: 'default' | 'danger';
  /** Material Symbols icon name */
  icon?: string;
  /** Extra detail shown between the description and the buttons, e.g. a list of what's affected */
  content?: ReactNode;
}

/** A pending confirmation, rendered by `<Form>` as a dialog. */
export interface FormConfirmation extends FormConfirmationRequest {
  confirm: () => void;
  cancel: () => void;
}

/** Thrown inside a submit when the user cancels its confirmation; never surfaces. */
class SubmitCancelledError extends Error {}

export interface UseZodFormProps<Schema extends ZodObjecty | undefined> extends UseFormProps<
  TypeOfOptional<Schema>
> {
  onSubmit?: (data: TypeOfOptional<Schema>) => unknown | Promise<unknown>;
  schema?: Schema;
  /**
   * Ask before submitting. Runs after validation passes; return a request to
   * show a confirmation dialog (rendered by `<Form>`), or null/false to submit
   * straight away. Cancelling leaves the form as it was: nothing is submitted
   * and no success or error is shown. Always called with the latest props.
   */
  confirm?: (data: TypeOfOptional<Schema>) => FormConfirmationRequest | null | undefined | false;
}

export function registerInput<TFormValues extends FieldValues, TPath extends Path<TFormValues>>(
  form: Pick<UseFormReturn<TFormValues>, 'register'>,
  path: TPath,
  options?: RegisterOptions<TFormValues, TPath>
) {
  return form.register(path, options);
}

export function registerToggle<TFormValues extends FieldValues, TPath extends Path<TFormValues>>(
  form: UseFormReturn<TFormValues>,
  path: TPath
) {
  const value = form.watch(path);
  return {
    name: path,
    checked: typeof value === 'boolean' ? value : /^true$/gi.test(value),
    value: 'true',
    onChange(checked: boolean) {
      //TODO: orignially  !!checked as PathValue<TFormValues, TPath>, but caused error after react hook form upgrade
      form.setValue(path, !!checked as any, {
        shouldDirty: true,
      });
    },
  };
}

export function registerDate<
  TPath extends string,
  TForm extends {
    watch(path: TPath): unknown;
    setValue(
      path: TPath,
      value: unknown,
      opts: {
        shouldDirty: true;
        shouldTouch: true;
      }
    ): unknown;
  },
>(form: TForm, path: TPath) {
  const value = form.watch(path);

  return {
    name: path,
    selected: value as Date | null,
    onChange: (date: Date | null): void => {
      const formattedDate = date
        ? isValid(date)
          ? format(new Date(date), 'yyyy-MM-dd')
          : null
        : null;

      form.setValue(path, formattedDate, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
  };
}

export function registerDateRange<
  TPath extends string,
  TForm extends {
    watch(path: TPath): unknown;
    setValue(
      path: TPath,
      value: unknown,
      opts: {
        shouldDirty: true;
        shouldTouch: true;
      }
    ): unknown;
  },
>(form: TForm, start: TPath, end: TPath) {
  return {
    start: registerDate(form, start),
    end: registerDate(form, end),
  };
}

export function registerAccountSelector<
  TPath extends string,
  TForm extends {
    watch(path: TPath): unknown;
    setValue(path: TPath, value: unknown, opts: { shouldDirty: true; shouldTouch: true }): unknown;
  },
>(form: TForm, path: TPath) {
  const value = form.watch(path);
  return {
    name: path,
    value: value ? { label: null, value: value as string } : undefined,
    onChange: (selected: { label: string; value: string } | null) => {
      form.setValue(path, selected?.value ?? '', {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
  };
}

export function isZodFormReturn<Schema extends ZodObjecty | undefined>(
  formReturn: UseFormReturn | UseZodFormReturn<Schema>
): formReturn is UseZodFormReturn<Schema> {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  return typeof formReturn.formState?.onSubmit === 'function';
}

export function isActiveZodFormReturn<Schema extends ZodObjecty | undefined>(
  formReturn: UseFormReturn | UseZodFormReturn<Schema>
): formReturn is Schema extends ZodObjecty ? UseZodFormReturn<Schema> : never {
  return isZodFormReturn(formReturn) && !!formReturn.formState;
}

export type ZodFormState<Schema extends ZodObjecty = ZodObjecty> = FormState<TypeOf<Schema>> & {
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
};

type ZodFormExtras<Schema extends ZodObjecty | undefined> = {
  formState: ZodFormState<Schema extends ZodObjecty ? Schema : SimpleObjectSchema>;
  schema: Schema extends ZodObjecty ? Schema : SimpleObjectSchema;
  /** The confirmation waiting on the user, if any */
  confirmation?: FormConfirmation | null;
};

export type UseZodFormReturn<Schema extends ZodObjecty | undefined = ZodObjecty | undefined> = Omit<
  UseFormReturn<TypeOfOptional<Schema>>,
  'formState'
> &
  ZodFormExtras<Schema>;

export const SIMPLE_OBJECT_SCHEMA = z.object({});
export type SimpleObjectSchema = typeof SIMPLE_OBJECT_SCHEMA;

export function useZodForm<Schema extends ZodObjecty | undefined = ZodObjecty | undefined>({
  schema,
  onSubmit,
  confirm,
  ...options
}: UseZodFormProps<Schema>): UseZodFormReturn<Schema> {
  // Read through a ref so the check always sees the latest props (e.g. a count that loads later)
  const confirmRef = useRef(confirm);
  confirmRef.current = confirm;
  const [confirmation, setConfirmation] = useState<FormConfirmation | null>(null);

  const resolver = zodResolver(schema ?? SIMPLE_OBJECT_SCHEMA) as unknown as Resolver<
    TypeOfOptional<Schema>
  >;
  const form = useForm<TypeOfOptional<Schema>>({
    ...options,
    resolver,
    defaultValues:
      options.defaultValues ??
      (schema ? (getZodDefaults(schema) as DefaultValues<TypeOfOptional<Schema>>) : undefined),
  });

  const extras = useMemo((): ZodFormExtras<Schema> => {
    const submit = form.handleSubmit(async (data) => {
      const request = confirmRef.current?.(data);
      if (request) {
        // The form stays "submitting" while the dialog waits for an answer
        const confirmed = await new Promise<boolean>((resolve) =>
          setConfirmation({
            ...request,
            confirm: () => resolve(true),
            cancel: () => resolve(false),
          })
        );
        setConfirmation(null);
        // Throwing marks the submit unsuccessful without setting an error
        if (!confirmed) throw new SubmitCancelledError();
      }

      try {
        await onSubmit?.(data);
      } catch (err) {
        form.control._subjects.state.next({
          isSubmitSuccessful: false,
        });
        form.control.setError('root.server', {
          type: 'response-error',
          message:
            err instanceof Error ? err.message : typeof err === 'string' ? err : 'unknown-error',
        });
      }
    });

    const formState: ZodFormState<NonNullable<Schema>> = {
      ...form.formState,
      onSubmit: async (e?: React.BaseSyntheticEvent) => {
        try {
          await submit(e);
        } catch (err) {
          if (!(err instanceof SubmitCancelledError)) throw err;
        }
      },
    };

    return { schema, formState, confirmation } as ZodFormExtras<Schema>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState, form.handleSubmit, form.control, onSubmit, schema, confirmation]);
  return useMemo((): UseZodFormReturn<Schema> => {
    return {
      ...form,
      ...extras,
    };
    // A new confirmation must reach <Form> so it can show the dialog
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, confirmation]);
}
